import React, { useState, useEffect } from "react";
import { Button } from "@/components/button";
import {
  Field,
  Label,
  Fieldset,
  Legend,
  ErrorMessage,
} from "@/components/fieldset";
import { Select } from "@/components/select";
import { Input } from "@/components/input";
import { getPaperTypeLabel } from "@/constants/exam";
import ExamPaperSelectModal from "../exam_sets/components/ExamPaperSelectModal";
import ExamPaperItem from "../exam_sets/components/ExamPaperItem";
import { useExamPaperList } from "@/apis/exam_papers/getExamPaperList";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
} from "@/components/table";
import type { IExamPaper } from "@/apis/exam_papers/types";

interface ExamAssignmentFormProps {
  initialValues: {
    title: string;
    exam_set?: {
      exam_type: string;
      items: {
        exam_paper_id: string;
        order: number;
      }[];
    };
    // Legacy fields for edit mode compatibility
    exam_set_id?: string;
  };
  isPending: boolean;
  onSubmit: (values: {
    title: string;
    exam_set: {
      exam_type: string;
      items: {
        exam_paper_id: string;
        order: number;
      }[];
    };
  }) => void;
  submitLabel?: string;
  readOnly?: boolean;
  isEdit?: boolean;
}

const ExamAssignmentForm: React.FC<ExamAssignmentFormProps> = ({
  initialValues,
  isPending,
  onSubmit,
  submitLabel = "Create Assignment",
  readOnly = false,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState(() => {
    // Handle legacy structure for edit mode
    if (!initialValues.exam_set) {
      return {
        title: initialValues.title || "",
        exam_set: {
          exam_type: "SAT",
          items: [],
        },
      };
    }
    return initialValues;
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Exam paper modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [modalPage, setModalPage] = useState(1);
  const [modalLimit] = useState(10);
  const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // 검색어 debounce 처리
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
      setModalPage(1);
    }, 300);
  }, [search]);

  // 서버 페이징 기반 시험지 목록 패칭
  const { data: modalExamPapersData, isLoading: isModalExamPapersLoading } =
    useExamPaperList({
      page: modalPage,
      limit: modalLimit,
      title: debouncedSearch,
      exam_type: formData.exam_set?.exam_type || "SAT",
    });

  const modalExamPapers: IExamPaper[] = modalExamPapersData?.items || [];
  const modalTotalCount = modalExamPapersData?.totalCount || 0;
  const modalTotalPages = Math.ceil(modalTotalCount / modalLimit);

  // 이미 선택된 시험지는 모달에서 제외
  const selectedIds = formData.exam_set?.items?.map((item) => item.exam_paper_id) || [];
  const availableExamPapers = modalExamPapers.filter(
    (p) => !selectedIds.includes(p.id)
  );

  // 필드 변경 핸들러
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    
    if (name === "exam_type") {
      // Reset exam papers when exam type changes
      setFormData((prev) => ({
        ...prev,
        exam_set: {
          exam_type: value,
          items: [], // Reset exam papers list
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Helper function to sort items: Reading and Writing first, then Math (SAT only)
  const sortExamPapers = (items: { exam_paper_id: string; order: number }[]) => {
    // Only apply sorting for SAT
    if (formData.exam_set?.exam_type !== "SAT") {
      return items; // No sorting for ACT/AP
    }

    const readingWritingItems = items.filter((item) => {
      const paperId = item.exam_paper_id;
      const paper = modalExamPapers.find((p) => p.id === paperId);
      return paper?.subject === "reading_writing";
    });

    const mathItems = items.filter((item) => {
      const paperId = item.exam_paper_id;
      const paper = modalExamPapers.find((p) => p.id === paperId);
      return paper?.subject === "math";
    });

    const otherItems = items.filter((item) => {
      const paperId = item.exam_paper_id;
      const paper = modalExamPapers.find((p) => p.id === paperId);
      return paper?.subject !== "reading_writing" && paper?.subject !== "math";
    });

    return [...readingWritingItems, ...mathItems, ...otherItems];
  };

  // Get existing paper types
  const getExistingPaperTypes = (): string[] => {
    return (formData.exam_set?.items || [])
      .map((item) => {
        const paper = modalExamPapers.find((p) => p.id === item.exam_paper_id);
        return paper?.subject;
      })
      .filter((subject): subject is string => Boolean(subject));
  };

  // Check if we can add more papers
  const canAddMorePapers = () => {
    // Only apply restrictions for SAT
    if (formData.exam_set?.exam_type !== "SAT") {
      return true; // No restrictions for ACT/AP
    }

    const existingTypes = getExistingPaperTypes();
    const hasReadingWriting = existingTypes.includes("reading_writing");
    const hasMath = existingTypes.includes("math");
    return !hasReadingWriting || !hasMath;
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      exam_set: {
        exam_type: prev.exam_set?.exam_type || "SAT",
        items: (prev.exam_set?.items || []).filter((_, i) => i !== index),
      },
    }));
  };

  const handleAddExamPaper = (paper: IExamPaper) => {
    const currentItems = formData.exam_set?.items || [];
    const newItem = {
      exam_paper_id: paper.id,
      order: currentItems.length + 1,
    };

    const updatedItems = [...currentItems, newItem];
    const sortedItems = sortExamPapers(updatedItems);

    // Update order based on sorted position
    const finalItems = sortedItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setFormData((prev) => ({
      ...prev,
      exam_set: {
        exam_type: prev.exam_set?.exam_type || "SAT",
        items: finalItems,
      },
    }));

    setIsModalOpen(false);
    setSearch("");
    setModalPage(1);
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    
    if (!formData.title) errors.title = "Please enter an assignment title";
    
    // Only validate exam_papers in create mode
    if (!isEdit) {
      if ((formData.exam_set?.items || []).length === 0) {
        errors.exam_papers = "Please select at least one exam paper";
      }
      
      // Ensure exam_set is defined before submitting in create mode
      if (!formData.exam_set) {
        errors.exam_set = "Exam set is required";
        setFormErrors(errors);
        return;
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Submit different data structure based on mode
    if (isEdit) {
      // In edit mode, only submit title
      onSubmit({
        title: formData.title,
      } as any);
    } else {
      // In create mode, submit full data
      onSubmit({
        title: formData.title,
        exam_set: formData.exam_set!,
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 bg-white rounded-md border border-gray-200"
    >
      <Fieldset>
        <Legend>Exam Assignment Details</Legend>
        <div className="mt-4 space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field>
              <Label htmlFor="title">Assignment Title*</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                disabled={isPending || readOnly}
                aria-invalid={!!formErrors.title}
                placeholder="Enter assignment title"
              />
              {formErrors.title && (
                <ErrorMessage>{formErrors.title}</ErrorMessage>
              )}
            </Field>

            {!isEdit && (
              <Field>
                <Label htmlFor="exam_type">Exam Type*</Label>
                <Select
                  id="exam_type"
                  name="exam_type"
                  value={formData.exam_set?.exam_type || "SAT"}
                  onChange={handleChange}
                  disabled={isPending || readOnly}
                >
                  <option value="SAT">SAT</option>
                  <option value="ACT">ACT</option>
                  <option value="AP">AP</option>
                </Select>
              </Field>
            )}
          </div>

          {/* Exam Papers Section */}
          {!isEdit && (
            <Field>
              <Label>Exam Papers*</Label>
              <div className="mt-2">
                {(formData.exam_set?.items || []).length > 0 ? (
                  <div className="px-4 border border-gray-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Title</TableHeader>
                          <TableHeader>Subject</TableHeader>
                          <TableHeader>Sections</TableHeader>
                          <TableHeader className="w-16">Action</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(formData.exam_set?.items || []).map((item, index) => (
                          <ExamPaperItem
                            key={item.exam_paper_id}
                            examPaperId={item.exam_paper_id}
                            index={index}
                            onRemove={() => removeItem(index)}
                            isLastItem={false}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                    No exam papers selected
                  </div>
                )}

                {!readOnly && canAddMorePapers() && (
                  <Button
                    type="button"
                    className="mt-4 w-full bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
                    onClick={() => setIsModalOpen(true)}
                    disabled={isPending}
                  >
                    + Add Exam Paper
                  </Button>
                )}

                {formData.exam_set?.exam_type === "SAT" && !canAddMorePapers() && (
                  <div className="mt-2 text-sm text-green-600">
                    ✓ Both Reading & Writing and Math papers are selected
                  </div>
                )}
              </div>
              {formErrors.exam_papers && (
                <ErrorMessage>{formErrors.exam_papers}</ErrorMessage>
              )}
            </Field>
          )}
        </div>
      </Fieldset>

      {/* Modals */}
      <ExamPaperSelectModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleAddExamPaper}
        excludeIds={selectedIds}
        existingTypes={getExistingPaperTypes()}
        examType={formData.exam_set?.exam_type || "SAT"}
      />

      <div className="mt-8 flex justify-end space-x-4">
        <Button
          type="submit"
          disabled={isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isPending ? "Saving..." : submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default ExamAssignmentForm;
